import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, Save, Mail } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/ImageUpload";
import { VenueModules } from "@/components/VenueModules";

export default function HubSettings() {
  const { setBiz, featureInfo, refreshFeatures } = useOutletContext();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setField = (k, v) => setForm({ ...form, [k]: v });

  const sendTest = async () => {
    try { const { data } = await api.post("/business/smtp/test", {}); toast.success(`Test email sent to ${data.to} via ${data.source} SMTP`); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, lat: form.lat === "" || form.lat == null ? null : parseFloat(form.lat), lng: form.lng === "" || form.lng == null ? null : parseFloat(form.lng), smtp_port: form.smtp_port ? parseInt(form.smtp_port, 10) : null };      const { data } = await api.put("/business", payload);
      setForm(data);
      setBiz(data);
      refreshFeatures?.();
      toast.success("Hub settings saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;

  return (
    <div className="space-y-8" data-testid="hub-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Hub Settings</h1>
          <p className="text-muted-foreground text-sm">Customise how guests experience your digital concierge.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-hub-btn" className="bg-primary text-primary-foreground">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-6 space-y-4">
          <h3 className="font-serif text-lg text-foreground">Identity</h3>
          <div><Label>Business name</Label><Input data-testid="field-name" value={form.name || ""} onChange={upd("name")} className="mt-1.5" /></div>
          <div><Label>Business type</Label><Input data-testid="field-type" value={form.business_type || ""} onChange={upd("business_type")} className="mt-1.5" /></div>
          <div><Label>Tagline</Label><Input data-testid="field-tagline" value={form.tagline || ""} onChange={upd("tagline")} className="mt-1.5" /></div>
          <div><Label>Welcome message</Label><Input data-testid="field-welcome" value={form.welcome_message || ""} onChange={upd("welcome_message")} className="mt-1.5" /></div>
          <div><Label>Description</Label><Textarea data-testid="field-description" value={form.description || ""} onChange={upd("description")} className="mt-1.5" rows={3} /></div>
          <div>
            <Label>Accent colour</Label>
            <div className="flex items-center gap-3 mt-1.5">
              <input type="color" data-testid="field-accent" value={form.accent_color || "#C5A880"} onChange={upd("accent_color")} className="w-12 h-10 rounded border border-border" />
              <Input value={form.accent_color || ""} onChange={upd("accent_color")} className="flex-1" />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <VenueModules form={form} setForm={setForm} featureInfo={featureInfo} onChanged={(data) => { refreshFeatures?.(); if (data) setBiz(data); }} />
          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <h3 className="font-serif text-lg text-foreground">Branding</h3>
            <div><Label className="mb-1.5 block">Cover image</Label><ImageUpload value={form.cover_image_url} onChange={(v) => setField("cover_image_url", v)} testId="cover-upload" label="cover" /></div>
            <div><Label className="mb-1.5 block">Logo</Label><ImageUpload value={form.logo_url} onChange={(v) => setField("logo_url", v)} testId="logo-upload" label="logo" /></div>
          </div>

          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <h3 className="font-serif text-lg text-foreground">Stay essentials</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Contact phone</Label><Input data-testid="field-phone" value={form.contact_phone || ""} onChange={upd("contact_phone")} className="mt-1.5" /></div>
              <div><Label>Checkout time</Label><Input data-testid="field-checkout" value={form.checkout_time || ""} onChange={upd("checkout_time")} className="mt-1.5" /></div>
            </div>
            <div><Label>Address</Label><Input data-testid="field-address" value={form.address || ""} onChange={upd("address")} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Wi-Fi name</Label><Input data-testid="field-wifi-name" value={form.wifi_name || ""} onChange={upd("wifi_name")} className="mt-1.5" /></div>
              <div><Label>Wi-Fi password</Label><Input data-testid="field-wifi-pass" value={form.wifi_password || ""} onChange={upd("wifi_password")} className="mt-1.5" /></div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg text-foreground">AI Concierge</h3>
                <p className="text-xs text-muted-foreground">Let guests tap "Ask AI" in chat for instant answers about your hub.</p>
              </div>
              <Switch checked={form.ai_concierge_enabled !== false} onCheckedChange={(v) => setField("ai_concierge_enabled", v)} data-testid="field-ai-enabled" />
            </div>
          </div>

          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg text-foreground">Quiet Hours</h3>
                <p className="text-xs text-muted-foreground">Pause guest push notifications overnight.</p>
              </div>
              <Switch checked={!!form.quiet_hours_enabled} onCheckedChange={(v) => setField("quiet_hours_enabled", v)} data-testid="field-quiet-enabled" />
            </div>
            {form.quiet_hours_enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>From</Label><Input data-testid="field-quiet-start" type="time" value={form.quiet_start || "22:00"} onChange={upd("quiet_start")} className="mt-1.5" /></div>
                <div><Label>Until</Label><Input data-testid="field-quiet-end" type="time" value={form.quiet_end || "07:00"} onChange={upd("quiet_end")} className="mt-1.5" /></div>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <h3 className="font-serif text-lg text-foreground">Location (for map & directions)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Latitude</Label><Input data-testid="field-lat" value={form.lat ?? ""} onChange={upd("lat")} className="mt-1.5" placeholder="51.7520" /></div>
              <div><Label>Longitude</Label><Input data-testid="field-lng" value={form.lng ?? ""} onChange={upd("lng")} className="mt-1.5" placeholder="-1.8290" /></div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <h3 className="font-serif text-lg text-foreground">SEO &amp; sharing</h3>
            <p className="text-xs text-muted-foreground -mt-2">How your hub appears in search results and the browser tab.</p>
            <div><Label>Page title</Label><Input data-testid="field-seo-title" value={form.seo_title || ""} onChange={upd("seo_title")} className="mt-1.5" placeholder={form.name ? `${form.name} · Guest Hub` : "Your hub name"} /></div>
            <div><Label>Meta description</Label><Textarea data-testid="field-seo-description" value={form.seo_description || ""} onChange={upd("seo_description")} className="mt-1.5" rows={2} placeholder="A short summary shown by search engines." /></div>
          </div>

          <div className="bg-card border border-border rounded-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg text-foreground">Your own mail server</h3>
                <p className="text-xs text-muted-foreground">Override the platform SMTP for your welcome emails.</p>
              </div>
              <Switch checked={!!form.smtp_enabled} onCheckedChange={(v) => setField("smtp_enabled", v)} data-testid="field-smtp-enabled" />
            </div>
            {form.smtp_enabled && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Host</Label><Input data-testid="field-smtp-host" value={form.smtp_host || ""} onChange={upd("smtp_host")} className="mt-1.5" /></div>
                  <div><Label>Port</Label><Input data-testid="field-smtp-port" type="number" value={form.smtp_port || ""} onChange={upd("smtp_port")} className="mt-1.5" placeholder="587" /></div>
                </div>
                <div><Label>Username</Label><Input data-testid="field-smtp-username" value={form.smtp_username || ""} onChange={upd("smtp_username")} className="mt-1.5" /></div>
                <div><Label>Password</Label><Input data-testid="field-smtp-password" type="password" value={form.smtp_password || ""} onChange={upd("smtp_password")} className="mt-1.5" /></div>
                <div><Label>From address</Label><Input data-testid="field-smtp-from" value={form.smtp_from || ""} onChange={upd("smtp_from")} className="mt-1.5" /></div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={sendTest} className="gap-1.5" data-testid="hub-smtp-test-btn"><Mail className="w-4 h-4" /> Send test email to me</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
